import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { ThemeContext } from '../hooks/ThemeContext';
import { UserContext } from '../hooks/UserContext';
import ContactForm from '../components/common/ContactForm';
import SectionTitle from '../components/common/SectionTitle';
import { User, Mail, MessageCircle, Palette } from 'lucide-react';

const Contact = () => {
  const { theme } = useContext(ThemeContext);
  const { isAuthenticated } = useContext(UserContext);
  const isDark = theme === 'dark';

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className={`${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 border-b border-white/10' : 'bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50 border-b border-gray-200'}`}>
        <div className="container mx-auto px-4 py-16 lg:py-20 max-w-7xl">
          <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] items-start">
            <div className="space-y-6 lg:pr-8">
              <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm shadow-xl ${isDark ? 'border-white/10 bg-white/5 text-gray-200' : 'border-gray-200 bg-white text-gray-700'}`}>
                <MessageCircle className="w-4 h-4 text-rose-400" />
                Contact premium et parcours clair
              </div>

              <SectionTitle 
                title="Nous contacter"
                subtitle={
                  isAuthenticated 
                    ? 'Choisissez le bon canal selon votre besoin, avec une expérience plus claire et plus rassurante.' 
                    : 'Contactez notre équipe pour toute question ou demande avec un formulaire simple et premium.'
                }
                align="left"
              />

              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  'Réponse rapide',
                  'Orientation claire',
                  'Support personnalisé'
                ].map((item) => (
                  <div key={item} className={`rounded-3xl border p-5 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
                    <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                      <MessageCircle className="w-5 h-5 text-rose-400" />
                    </div>
                    <p className={`${isDark ? 'text-gray-200' : 'text-gray-700'} font-medium`}>{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className={`rounded-3xl border p-6 md:p-8 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
              <h3 className="text-2xl font-bold mb-4">Avant d’envoyer votre message</h3>
              <div className="space-y-4">
                {[
                  'Choisissez le bon service pour une réponse plus rapide.',
                  'Décrivez précisément votre besoin ou votre projet.',
                  'Connectez-vous pour accéder aux options avancées de contact.'
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                      <MessageCircle className="w-4 h-4 text-rose-400" />
                    </div>
                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'} leading-7`}>{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-7xl">

        {isAuthenticated ? (
          /* Options de contact pour utilisateurs connectés */
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Contact Admin */}
              <div 
                className={`group relative overflow-hidden p-8 rounded-3xl border shadow-xl transition-all duration-300 hover:-translate-y-1 ${
                  isDark 
                    ? 'bg-white/5 border-white/10 hover:border-white/20' 
                    : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-2xl'
                }`}
              >
                <div className="relative mb-6">
                  <div className="inline-flex p-4 rounded-2xl transition-transform duration-300 group-hover:scale-110 bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                    <Mail className={`w-8 h-8 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
                  </div>
                  <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full animate-pulse ${
                    isDark ? 'bg-rose-500' : 'bg-rose-400'
                  }`} />
                </div>

                {/* Contenu */}
                <h3 className="text-2xl font-bold mb-3 flex items-center gap-2">
                  Support & Administration
                  <MessageCircle className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
                </h3>
                
                <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Questions générales, support technique, demandes administratives, ou aide avec votre compte.
                </p>

                <div className={`flex flex-col gap-2 mb-6 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-rose-400' : 'bg-rose-600'}`} />
                    Réponse sous 24h
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-rose-400' : 'bg-rose-600'}`} />
                    Support prioritaire
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-rose-400' : 'bg-rose-600'}`} />
                    Aide personnalisée
                  </div>
                </div>

                <div className={`px-4 py-3 rounded-xl text-sm font-medium ${
                  isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 text-gray-300 border border-white/10' : 'bg-slate-50 text-gray-700 border border-gray-200'
                }`}>
                  ↓ Formulaire ci-dessous
                </div>
              </div>

              {/* Contact Créateur */}
              <div 
                className={`group relative overflow-hidden p-8 rounded-3xl border shadow-xl transition-all duration-300 hover:-translate-y-1 ${
                  isDark 
                    ? 'bg-white/5 border-white/10 hover:border-white/20' 
                    : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-2xl'
                }`}
              >
                <div className="absolute top-4 right-4 opacity-20">
                  <Palette className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>

                <div className="relative mb-6">
                  <div className="inline-flex p-4 rounded-2xl transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6 bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                    <User className={`w-8 h-8 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  </div>
                </div>

                {/* Contenu */}
                <h3 className="text-2xl font-bold mb-3 flex items-center gap-2">
                  Projets & Créations
                  <Palette className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                </h3>
                
                <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Commandes personnalisées, projets sur-mesure, collaborations créatives, ou discussions avec nos artistes.
                </p>

                <div className={`flex flex-col gap-2 mb-6 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`} />
                    Échange direct avec le créateur
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`} />
                    Devis personnalisé
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`} />
                    Projets sur-mesure
                  </div>
                </div>

                <Link 
                  to="/contacter-createur"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 group-hover:gap-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white shadow-xl"
                >
                  Choisir un créateur
                  <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </>
        ) : (
          /* Utilisateur déconnecté - Seulement contact admin */
          <div className="max-w-3xl mx-auto mb-12">
            <div 
              className={`p-8 rounded-3xl border shadow-xl ${
                isDark 
                  ? 'bg-white/5 border-white/10' 
                  : 'bg-white border-gray-200'
              }`}
            >
              <div className="inline-flex p-4 rounded-2xl mb-6 bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                <Mail className={`w-8 h-8 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
              </div>
              
              <h3 className="text-2xl font-bold mb-3">Contactez notre équipe</h3>
              <p className={`mb-6 leading-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Envoyez-nous votre message via le formulaire ci-dessous. Notre équipe vous répondra dans les plus brefs délais.
              </p>

              <div className={`px-4 py-3 rounded-xl border ${
                isDark 
                  ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 border-white/10 text-blue-300' 
                  : 'bg-blue-50 border-blue-200 text-blue-700'
              }`}>
                <p className="text-sm font-medium">
                  💡 Connectez-vous pour accéder à plus d'options de contact et contacter directement nos créateurs
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Formulaire de contact */}
        <ContactForm variant="default" />

        {!isAuthenticated && (
          <div className={`mt-8 p-6 rounded-3xl border shadow-xl ${
            isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'
          }`}>
            <p className="text-center">
              Vous avez déjà un compte ?{' '}
              <Link to="/login" className={`font-semibold hover:underline ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>
                Connectez-vous
              </Link>
              {' '}pour un support prioritaire et plus d'options de contact.
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default Contact;
